import {Component, Inject, OnInit} from '@angular/core';
import {UntilDestroy} from '@ngneat/until-destroy';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {Application, NewWebService, SelectWebServicesDialogData, SelectWebServicesDialogResult, WEB_SERVICE_STALE_AFTER_DAYS, isWebServiceStale, webServiceAgeInDays} from '@portal-workspace/grow-shared-library';
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { formControlErrorKeys, formControlErrorMessage, setupUntilDestroy } from '../component-utils';
import { MatButtonModule } from '@angular/material/button';
import { FlexModule } from '@angular/flex-layout/flex';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { NgxCurrencyDirective } from 'ngx-currency'
import { InputMaskModule } from '@ngneat/input-mask';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { LocalBankAccountDetailComponent } from '../local-bank-account-detail-component/local-bank-account-detail.component';
import { CurrencyInputComponent } from '../currency-selection-component/currency-input.component';
import { MessageBoxComponent } from '../message-box/message-box.component';
import _ from "lodash";
import moment from 'moment';
import { ApplicationDialogService } from './application-dialog.service';

interface WebServiceCheckBox {
  id: number;
  check: boolean;
  webService: NewWebService;
  applicant: string;
  lastUpdatedDate: string;
  ageInDays: number | null;
  stale: boolean;
}

@UntilDestroy({arrayName: 'subscriptions'})
@Component({
    templateUrl: './select-web-services.dialog.html',
    styleUrls: ['./select-web-services.dialog.scss'],
    standalone: true,
    imports: [FormsModule, ReactiveFormsModule, MatCheckboxModule, InputMaskModule, NgxCurrencyDirective, LocalBankAccountDetailComponent, MatInputModule, MatFormFieldModule, MatSelectModule, MatOptionModule, FlexModule, MatButtonModule, MatDialogModule, CurrencyInputComponent, MessageBoxComponent]
})
export class SelectWebServicesDialog implements OnInit {

  errorKeys = formControlErrorKeys;
  errorMessage = formControlErrorMessage;

  subscriptions: Subscription[] = [];

  webServices: NewWebService[] = [];
  application!: Application;
  checkBoxes: WebServiceCheckBox[] = [];
  hasStaleWebServices = false;
  readonly staleAfterDays = WEB_SERVICE_STALE_AFTER_DAYS;
  readonly productTypeOptions: Array<'LOW DOC' | 'EASY DOC' | 'FULL DOC'> = ['LOW DOC', 'EASY DOC', 'FULL DOC'];
  formControlProductType!: FormControl<'LOW DOC' | 'EASY DOC' | 'FULL DOC' | null>;
 
  constructor(@Inject(MAT_DIALOG_DATA) public dialogData: SelectWebServicesDialogData,
              private formBuilder: FormBuilder,
              private dialogService: ApplicationDialogService,
              private dialogRef: MatDialogRef<SelectWebServicesDialog, SelectWebServicesDialogResult>) {
    this.webServices = dialogData.webServices;
    this.application = dialogData.application;
    this.checkBoxes = this.webServices.map(webService => { 
      let applicant = '';
      if (webService.level === 'individual') {
        applicant = `${webService.individualFirstName} ${webService.individualLastName} (${moment(webService.individualDoB).format("DD/MM/YYYY")})`;
      } else {
        const entity = (dialogData.parties?.commercialEntities ?? []).find(entity => webService.ABN ? (webService.ABN === entity.abn) : (webService.ACN === entity.acn))
        if (entity) {
          applicant = `${entity.legalName}`;
        }
      }

      return {
        id: webService.id as number,
        check: false,
        webService: webService,
        applicant: applicant,
        lastUpdatedDate: moment(webService.lastUpdatedTime).format("DD/MM/YYYY"),
        ageInDays: webServiceAgeInDays(webService.lastUpdatedTime),
        stale: isWebServiceStale(webService.lastUpdatedTime),
      }
    });
    this.hasStaleWebServices = this.checkBoxes.some(checkBox => checkBox.stale);
    this.formControlProductType = this.formBuilder.control(dialogData.productType ?? null);
  }

  ngOnInit(): void {
    setupUntilDestroy(this);
  }

  isAssetFinanceApplication(): boolean {
    return this.application.ApplicationType === 'AssetFinance';
  }

  canConfirm(): boolean {
    if (!this.isAssetFinanceApplication()) {
      return true;
    }
    return !!this.formControlProductType.value;
  }

  submit() {
    this.dialogRef.close({
      readyForSubmission: true,
      webServiceIds: this.checkBoxes.filter(obj => obj.check).map(obj => obj.id),
      productType: this.formControlProductType.value ?? undefined,
    })
  }

  cancel() {
    this.dialogRef.close();
  }

  update(check: boolean, index: number) {
    this.checkBoxes[index] = {
      ...this.checkBoxes[index],
      check: check
    }
  }
}
